import { Injectable } from '@angular/core';
import {
  ActionCableChannel,
  ActionCableChannelType
} from '../../core/declarations/action_cable.interface';
import { Tenant } from '../../core/declarations/tenant.interface';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { TenantState } from '../../core/states/tenant-state/tenant.state';
import { environment } from '@bling-fe/shared/env';
import { ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Consumer, createConsumer, Subscription } from '@rails/actioncable';
import { from, MonoTypeOperatorFunction, Observable, Subject } from 'rxjs';
import { filter, map, retry, share, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { TranslateService } from '@ngx-translate/core';

export const connected = Symbol();
export const disconnected = Symbol();
export type Connected = typeof connected;
export type Disconnected = typeof disconnected;

export interface ConnectionEvent {
  type: 'connected' | 'disconnected';
  channel: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
}

/**
 * ActionCableRepository provides WebSocket functionality using Rails ActionCable.
 *
 * Usage examples:
 *
 * 1. Subscribe to a channel and its messages:
 * ```typescript
 * this.actionCableRepository.subscribeToChannel('ChatChannel', { room: 'general' })
 *   .subscribe(event => {
 *     if (event === connected) {
 *       console.log('Connected to channel');
 *     } else if (event === disconnected) {
 *       console.log('Disconnected from channel');
 *     } else {
 *       console.log('Received message:', event);
 *     }
 *   });
 * ```
 *
 * 2. Subscribe to connection events for a specific channel:
 * ```typescript
 * this.actionCableRepository.subscribeToChannelConnectionEvents('ChatChannel', { room: 'general' })
 *   .subscribe(event => {
 *     console.log(`Chat channel ${event.type}`);
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ActionCableRepository {
  private actionCableSubscriptions = new Map<string, Subscription>();
  private lastSuccessfulConnectionTime: number | null = null;
  private lastResetAt: number | null = null;
  private connectionEventsSubject = new Subject<ConnectionEvent>();

  public connectionEvents$ = this.connectionEventsSubject.asObservable();

  constructor(
    private authenticationService: AuthenticationService,
    private tenantStore: Store<TenantState>,
    private toastController: ToastController,
    private translate: TranslateService
  ) {}

  public subscribeToChannel<T extends ActionCableChannel>(
    channel: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any
  ): Observable<Connected | Disconnected | ActionCableChannelType<T>> {
    return this.getWsConsumer().pipe(
      switchMap(consumer => {
        return this.channelObservable<T>(channel, params, consumer);
      })
    );
  }

  /**
   * Subscribe to connection events for a specific channel
   */
  public subscribeToChannelConnectionEvents<T extends ActionCableChannel>(
    channel: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any
  ): Observable<ConnectionEvent> {
    const channelKey = channel + JSON.stringify(params);
    return this.connectionEvents$.pipe(
      filter(event => event.channel === channelKey)
    );
  }

  public sendMessage<T extends ActionCableChannel>(
    channel: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any,
    message: object
  ): void {
    const subscription = this.actionCableSubscriptions.get(
      channel + JSON.stringify(params)
    );
    if (!subscription) {
      throw 'Subscription not found, intialize subscription first';
    }
    subscription.send(message);
  }

  /**
   * Use in pipe on observable from `subscribeToChannel` method. E.g.:
   * this.acr.subscribeToChannel(...).pipe(this.acr.defaltWsConnectionErrorHandler()).subscribe(...)
   */
  public reconnectionHandler<T>(): MonoTypeOperatorFunction<T> {
    return retry({
      delay: (_error, retryCount) => {
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes

        // Reset retry count if it's been more than 5 minutes since last successful connection
        // AND we haven't already reset in the last 5 minutes
        let effectiveRetryCount = retryCount;
        const shouldReset =
          this.lastSuccessfulConnectionTime &&
          now - this.lastSuccessfulConnectionTime > fiveMinutes &&
          (!this.lastResetAt || now - this.lastResetAt > fiveMinutes);

        if (shouldReset) {
          effectiveRetryCount = 1; // Reset to first attempt
          this.lastResetAt = now; // Track when we reset
        }

        const baseDelay = 2000; // 2 seconds
        const maxDelay = 60000; // 1 minute
        const exponentialDelay = Math.min(
          baseDelay * Math.pow(2, effectiveRetryCount - 1),
          maxDelay
        );

        // Add jitter: random value between 0.5 and 1.5 times the calculated delay
        const jitter = 0.5 + Math.random();
        const backoffDelay = Math.round(exponentialDelay * jitter);

        return new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    });
  }

  private channelObservable<T extends ActionCableChannel>(
    channel: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any,
    consumer: Consumer
  ): Observable<Connected | Disconnected | ActionCableChannelType<T>> {
    return new Observable(s => {
      const channelKey = channel + JSON.stringify(params);
      let acSubscription = this.actionCableSubscriptions.get(channelKey);
      acSubscription ||= consumer.subscriptions.create(
        { channel, ...params },
        {
          connected: () => {
            this.lastSuccessfulConnectionTime = Date.now();
            this.lastResetAt = null; // Clear reset timer on successful connection

            // Emit connection event
            this.connectionEventsSubject.next({
              type: 'connected',
              channel: channelKey,
              params
            });

            s.next(connected);
          },
          disconnected: () => {
            // Emit disconnection event
            this.connectionEventsSubject.next({
              type: 'disconnected',
              channel: channelKey,
              params
            });

            s.next(disconnected);
          },
          messages_list: data => s.next(data),
          received: data => s.next(data),
          rejected: () => s.error(`Websocket ${channel} rejected`)
        }
      );
      this.actionCableSubscriptions.set(channelKey, acSubscription);

      // unsubscribe from the websocket channel on observable unsubscribe
      return () => {
        acSubscription.unsubscribe();
        this.actionCableSubscriptions.delete(channelKey);
      };
    });
  }

  private getWsConsumer(): Observable<Consumer> {
    return this.tenantStore.select(getCurrentTenant).pipe(
      filter(x => x !== 'loading' && x !== null),
      // get auth data for initialising ws connection
      switchMap((tenant: Tenant) =>
        from(this.authenticationService.getAuthData()).pipe(
          map(authData => ({
            access_token: authData.accessToken,
            uid: authData.uid,
            client: authData.client,
            tenant: tenant.subdomain
          }))
        )
      ),
      // get connected ws consumer
      switchMap(wsConnectionParams => {
        const wsConnectionParamsString = new URLSearchParams(
          wsConnectionParams
        ).toString();

        return new Observable<Consumer>(s => {
          const consumer = createConsumer(
            `${environment.websocketUrl}?${wsConnectionParamsString}`
          );
          consumer.connect();
          s.next(consumer);
          // disconnect consumer on last unsubscribe
          return () => consumer.disconnect();
        });
      }),
      // one consumer can be shared across multiple channels
      // this tracks #of subscribed channels. If we unsubscribe from all channels, consumer.disconnect() is called
      share()
    );
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.connectionEventsSubject.complete();
  }
}
